import Ui3HandoffResource from 'App/babel/resources/Ui3Handoff';
import { noRedirectRoutes } from 'App/babel/resources/Resource';
import { hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import { consumeLoginReturnUrl, getLoginReturnUrl } from 'App/helpers/loginHelper';

const LOGIN_ROUTE = '/login';

// Per tab: a reload, a version reload or a re-login keeps a user who asked for the legacy app
// (`?legacyUi=1`) there, and an environment with no ui-3 is asked once rather than on every boot.
const STAY_IN_LEGACY_KEY = 'Upsales.stayInLegacyUi';

const currentRoute = () => window.location.hash.replace(/^#/, '');

// Read on call, not at import: tests that auto-mock Resource load this module with no list.
// The login form is not one of them: initSession runs while it is still the route, right after
// a successful sign-in.
const uninterruptiblePaths = () => [
	...noRedirectRoutes.filter(path => path !== LOGIN_ROUTE),
	'/oauthLanding',
	'/externalSSO'
];

// A route the legacy app has to finish itself: an SSO handshake into Zendesk/Looker
// (`/externalSSO/:site`, or `?externalSSO=` on the start page or the login form), or a
// `?returnTo=` state the login form goes on to.
const isUninterruptible = (route: string) => {
	const query = new URLSearchParams(route.split('?')[1] ?? '');
	return (
		query.has('externalSSO') || query.has('returnTo') || uninterruptiblePaths().some(path => route.startsWith(path))
	);
};

const stayInLegacy = () => sessionStorage.setItem(STAY_IN_LEGACY_KEY, '1');

const wantsLegacy = () => {
	if (new URLSearchParams(window.location.search).get('legacyUi') === '1') {
		stayInLegacy();
	}
	return sessionStorage.getItem(STAY_IN_LEGACY_KEY) === '1';
};

// The start page is where an SSO sign-in lands (saml.js redirects to `/#/`), and like the login
// form it goes on to the deep link the user was bounced from - the `tmpStart` state reads it.
const isLoginOrStart = (route: string) => route.startsWith(LOGIN_ROUTE) || route === '' || route === '/';

const destination = () => {
	if (!isLoginOrStart(currentRoute())) {
		return window.location.hash;
	}
	const returnUrl = getLoginReturnUrl();
	return returnUrl ? new URL(returnUrl).hash : '';
};

export function shouldRedirectToUi3(): boolean {
	return (
		hasSoftDeployAccess('UI3_REDIRECT') &&
		!wantsLegacy() &&
		window.self === window.top &&
		!isUninterruptible(currentRoute()) &&
		!isUninterruptible(destination().replace(/^#/, ''))
	);
}

/** True when the page is on its way to ui-3, and nothing else should run. */
export async function handOffToUi3(): Promise<boolean> {
	if (!shouldRedirectToUi3()) {
		return false;
	}
	try {
		const url = await Ui3HandoffResource.create(destination());
		consumeLoginReturnUrl();
		window.location.replace(url);
		return true;
	} catch (err) {
		if ((err as { response?: { status?: number } })?.response?.status === 404) {
			// No ui-3 in this environment (alpha, sandbox): an answer, not a fault.
			stayInLegacy();
		} else {
			console.error('Failed to hand the session to ui-3', err);
		}
		return false;
	}
}
